/**
 * Search history persisted in localStorage.
 *
 * Storage contract (shared with GlobalSearch component):
 * - Key: `@nipsys:global-search-history`
 * - Shape: `SearchHistoryEntry[]` (newest first, max MAX_HISTORY_ENTRIES)
 * - Legacy compat: plain `string[]` entries (pre-timestamp format) are
 *   normalized on read and rewritten on the next mutation.
 */
import { useCallback, useEffect, useState } from "react";

const HISTORY_KEY = "@nipsys:global-search-history";

export const MAX_HISTORY_ENTRIES = 10;

export interface SearchHistoryEntry {
  /** The executed query. */
  q: string;
  /** Epoch millis when the search was executed (0 for legacy entries). */
  ts: number;
}

function normalizeEntry(raw: unknown): SearchHistoryEntry | null {
  if (typeof raw === "string") {
    const q = raw.trim();
    return q ? { q, ts: 0 } : null;
  }
  if (raw && typeof raw === "object") {
    const item = raw as Record<string, unknown>;
    if (typeof item.q !== "string" || !item.q.trim()) return null;
    return {
      q: item.q.trim(),
      ts: typeof item.ts === "number" && Number.isFinite(item.ts) ? item.ts : 0,
    };
  }
  return null;
}

function readHistory(): SearchHistoryEntry[] {
  try {
    const stored = localStorage.getItem(HISTORY_KEY);
    if (!stored) return [];
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    const normalized = parsed
      .map(normalizeEntry)
      .filter((entry): entry is SearchHistoryEntry => entry !== null);
    // Dedupe (case-insensitive, keeping the most recent occurrence) and cap.
    const seen = new Set<string>();
    const deduped: SearchHistoryEntry[] = [];
    for (const entry of normalized) {
      const key = entry.q.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(entry);
    }
    return deduped.slice(0, MAX_HISTORY_ENTRIES);
  } catch {
    // Corrupted JSON or unavailable storage — start clean instead of crashing.
    return [];
  }
}

export interface UseSearchHistoryResult {
  entries: SearchHistoryEntry[];
  /** Records an executed query (dedupe + cap handled internally). */
  add: (query: string) => void;
  /** Removes a single entry by query text. */
  remove: (query: string) => void;
  /** Clears the whole history. */
  clear: () => void;
}

export function useSearchHistory(): UseSearchHistoryResult {
  const [entries, setEntries] = useState<SearchHistoryEntry[]>([]);

  // Hydrate on mount and write the normalized list back, migrating legacy
  // plain-string entries to the {q, ts} shape on first load.
  useEffect(() => {
    const normalized = readHistory();
    setEntries(normalized);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(normalized));
    } catch {
      // Storage blocked — state is already normalized in memory.
    }
  }, []);

  const persist = useCallback((next: SearchHistoryEntry[]) => {
    setEntries(next);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
      // Storage full/blocked — keep in-memory state, ignore the write failure.
    }
  }, []);

  const add = useCallback(
    (query: string) => {
      const q = query.trim();
      if (!q) return;
      // Read fresh from storage to avoid stale-closure duplication
      // (component is a singleton, so fresh reads are safe).
      const current = readHistory();
      const deduped = current.filter((entry) => entry.q.toLowerCase() !== q.toLowerCase());
      persist([{ q, ts: Date.now() }, ...deduped].slice(0, MAX_HISTORY_ENTRIES));
    },
    [persist],
  );

  const remove = useCallback(
    (query: string) => {
      persist(readHistory().filter((entry) => entry.q !== query));
    },
    [persist],
  );

  const clear = useCallback(() => {
    persist([]);
  }, [persist]);

  return { entries, add, remove, clear };
}
